import type { ReactNode } from 'react';
import { Info } from 'lucide-react';
import { cn } from '../../lib/utils';
import { Button } from '../ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';

export interface DescriptionHintProps {
  children: ReactNode;
  /** Accessible name for the trigger button. */
  ariaLabel: string;
  className?: string;
}

/**
 * Mobile-only description affordance: an info icon that opens a popover with
 * helper copy. Pair with an inline description hidden below `sm` on the same
 * surface (`PageHeader`, `SectionCard`).
 */
export function DescriptionHint({ children, ariaLabel, className }: DescriptionHintProps) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="toolbar-icon"
          aria-label={ariaLabel}
          className={cn('shrink-0 text-muted-foreground', className)}
          data-testid="description-hint-trigger"
        >
          <Info className="size-3.5" aria-hidden="true" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        side="bottom"
        aria-label={ariaLabel}
        className="text-sm text-muted-foreground"
        data-testid="description-hint-content"
      >
        {children}
      </PopoverContent>
    </Popover>
  );
}
